<template>
    <nio-modal
        body-class-name="p-0"
        :centered="false"
        name="user"
        size="lg"
        :title="modalTitle"
        ref="modal"
        @closed="onClosed"
        @open="onOpen"
    >
        <nio-tabs class="mb-4" :tabs="tabs" v-model="activeTab" />

        <div class="p-6">
            <div class="flex flex-col" v-if="activeTab == 'profile'">
                <div class="flex items-center text-3xl mb-4">
                    <nio-icon
                        class="mr-2"
                        name="regular/single-neutral-id-card-3"
                    />
                    <h2>
                        {{ $t('configs.rightPanel.profile.subtitles.profile') }}
                    </h2>
                </div>

                <user-info
                    :in-edit-mode="isEdit"
                    v-model="user"
                />
            </div>

            <div v-if="activeTab == 'notifications'">
                <div class="flex items-center text-3xl mb-4">
                    <nio-icon
                        class="mr-2"
                        name="regular/alarm-bell"
                    />
                    <h2>
                        {{ $t('configs.rightPanel.notifications.subtitles.notifications') }}
                    </h2>
                </div>

                <div class="flex flex-col w-full border border-gray-200 rounded">
                    <div class="flex items-center py-4 border-b border-gray-200 rounded-t bg-gray-100">
                        <div class="flex-2 px-4">
                            {{ $t('configs.rightPanel.notifications.type') }}
                        </div>
                        <div class="flex-1 flex justify-center items-center px-4">
                            <nio-icon class="mr-2" name="regular/screen-1" />
                            <span>{{ $t('configs.rightPanel.notifications.screen') }}</span>
                        </div>
                        <div class="flex-1 flex justify-center items-center px-4">
                            <nio-icon class="mr-2" name="regular/messages-bubble-text_1" />
                            <span>{{ $t('configs.rightPanel.notifications.sms') }}</span>
                        </div>
                    </div>

                    <div class="flex flex-col items-center w-full h-full">
                        <div class="flex items-center w-full py-4 | transition duration-300 hover:bg-gray-100">
                            <div class="flex-2 px-4">
                                <span>
                                    {{ $t('configs.rightPanel.notifications.newMessageConfirmation') }}
                                </span>
                            </div>

                            <div class="flex-1 flex justify-center items-center px-4">
                                <input
                                    class="form-checkbox"
                                    type="checkbox"
                                    v-model="user.options.notifications.message_received.screen"
                                >
                            </div>

                            <div class="flex-1 flex justify-center items-center px-4">
                                <input
                                    class="form-checkbox"
                                    type="checkbox"
                                    v-model="user.options.notifications.message_received.email"
                                >
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="activeTab == 'messages'">
                <div class="flex items-center text-3xl mb-4">
                    <nio-icon
                        class="mr-2"
                        name="regular/messages-bubble-settings"
                    />
                    <h2>
                        {{ $t('configs.rightPanel.messages.subtitles.messages') }}
                    </h2>
                </div>

                <automated-messages :show-payment-message="authUser.account.payment_module" :user="user" />
            </div>

            <div v-if="activeTab == 'permissions'">
                <div class="flex items-center text-3xl mb-4">
                    <nio-icon
                        class="mr-2"
                        name="regular/key-lock"
                    />
                    <h2>
                        {{ $t('configs.rightPanel.users.permissions.subtitles.permissions') }}
                    </h2>
                </div>

                <div class="flex flex-col w-full border border-gray-200 rounded">
                    <div class="flex items-center py-4 border-b border-gray-200 rounded-t bg-gray-100">
                        <div class="flex-2 px-4">
                            {{ $t('configs.rightPanel.users.permissions.type') }}
                        </div>

                        <div class="flex-1 flex justify-center items-center px-4">
                            <nio-icon class="mr-2" name="regular/key-lock" />
                            <span>
                                {{ $t('configs.rightPanel.users.permissions.allowed') }}
                            </span>
                        </div>
                    </div>

                    <div class="flex flex-col items-center w-full h-full">
                        <div class="flex items-center w-full py-4 | transition duration-300 hover:bg-gray-100">
                            <div class="flex-2 px-4">
                                <span>
                                    {{ $t('configs.rightPanel.users.permissions.teamSettings') }}
                                </span>
                            </div>

                            <div class="flex-1 flex justify-center items-center px-4">
                                <input
                                    class="form-checkbox"
                                    type="checkbox"
                                    value="manage team settings"
                                    v-model="user.permissions"
                                >
                            </div>
                        </div>

                        <div class="flex items-center w-full py-4 | transition duration-300 hover:bg-gray-100">
                            <div class="flex-2 px-4">
                                <span>
                                    {{ $t('configs.rightPanel.users.permissions.otherUsersConversations') }}
                                </span>
                            </div>

                            <div class="flex-1 flex justify-center items-center px-4">
                                <input
                                    class="form-checkbox"
                                    type="checkbox"
                                    value="view other users conversations"
                                    v-model="user.permissions"
                                >
                            </div>
                        </div>

                        <div class="flex items-center w-full py-4 | transition duration-300 hover:bg-gray-100" v-if="authUser.account.appointment_module">
                            <div class="flex-2 px-4">
                                <span>
                                    {{ $t('configs.rightPanel.users.permissions.appointmentModule') }}
                                </span>
                            </div>

                            <div class="flex-1 flex justify-center items-center px-4">
                                <input
                                    class="form-checkbox"
                                    type="checkbox"
                                    value="appointment module"
                                    v-model="user.permissions"
                                >
                            </div>
                        </div>

                        <div class="flex items-center w-full py-4 | transition duration-300 hover:bg-gray-100" v-if="authUser.account.campaign_module">
                            <div class="flex-2 px-4">
                                <span>
                                    {{ $t('configs.rightPanel.users.permissions.campaignModule') }}
                                </span>
                            </div>

                            <div class="flex-1 flex justify-center items-center px-4">
                                <input
                                    class="form-checkbox"
                                    type="checkbox"
                                    value="campaign module"
                                    v-model="user.permissions"
                                >
                            </div>
                        </div>

                        <div class="flex items-center w-full py-4 | transition duration-300 hover:bg-gray-100" v-if="authUser.account.payment_module">
                            <div class="flex-2 px-4">
                                <span>
                                    {{ $t('configs.rightPanel.users.permissions.paymentModule') }}
                                </span>
                            </div>

                            <div class="flex-1 flex justify-center items-center px-4">
                                <input
                                    class="form-checkbox"
                                    type="checkbox"
                                    value="payment module"
                                    v-model="user.permissions"
                                >
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <error-message class="mt-6" />
        </div>

        <template #footer>
            <nio-button :disabled="saving" @click="close">
                {{ $t('global.general.cancel') }}
            </nio-button>
            <nio-button
                color="blue"
                :loading="saving"
                @click="onSave"
            >
                {{ saveButtonText }}
            </nio-button>
        </template>
    </nio-modal>
</template>

<script>
    import { mapActions, mapState, mapMutations } from 'vuex';
    import cloneDeep from 'lodash/cloneDeep.js';
    import { merge } from '@/utils/index.js';
    import UserInfo from '@/components/UserInfo.vue';
    import AutomatedMessages from '@/components/User/AutomatedMessages.vue';

    const defaultUser = {
        id: null,
        related_id: null,
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        locale: 'fr',
        timezone: '',
        active: true,
        options: {
            unsubscribe_message: {
                fr: '',
                en: '',
            },
            confirmation_message: {
                fr: '',
                en: '',
            },
            crm_confirmation_message: {
                fr: '',
                en: '',
            },
            payment_message: {
                fr: '',
                en: '',
            },
            append_confirmation_message: true,
            quick_send: true,
            notifications: {
                message_received: {
                    email: false,
                    screen: true,
                },
            },
        },
        permissions: [],
    };

    export default {
        components: {
            AutomatedMessages,
            UserInfo,
        },

        data() {
            return {
                saving: false,
                user: cloneDeep(defaultUser),
                activeTab: 'profile',
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('setting', [
                'editingUser',
            ]),

            modalTitle() {
                return this.isEdit ? this.$t('configs.rightPanel.users.userModal.editTitle') : this.$t('configs.rightPanel.users.userModal.addTitle');
            },

            isEdit() {
                return !empty(this.editingUser);
            },

            showPermissions() {
                return this.user.id != this.authUser.id;
            },

            saveButtonText() {
                return this.isEdit ? this.$t('global.general.save') : this.$t('global.general.create');
            },

            tabs() {
                return [
                    {
                        value: 'profile',
                        text: this.$t('configs.rightPanel.users.leftNav.profile'),
                    },
                    {
                        value: 'notifications',
                        text: this.$t('configs.rightPanel.users.leftNav.notifications'),
                        condition: this.isEdit,
                    },
                    {
                        value: 'messages',
                        text: this.$t('configs.rightPanel.users.leftNav.messages'),
                        condition: this.isEdit,
                    },
                    {
                        value: 'permissions',
                        text: this.$t('configs.rightPanel.users.leftNav.permissions'),
                        condition: this.showPermissions,
                    },
                ];
            },
        },

        watch: {
            editingUser() {
                if (!empty(this.editingUser)) {
                    merge(this.user, this.editingUser);

                    this.setPermissions(this.editingUser);
                }
            },
        },

        methods: {
            ...mapActions('setting', [
                'createUser',
                'updateUser',
            ]),

            ...mapMutations('setting', [
                'setEditingUser',
            ]),

            async onSave() {
                try {
                    this.saving = true;

                    if (this.isEdit) {
                        await this.onUpdate();
                    } else {
                        await this.onCreate();
                    }

                    this.close();
                } catch (error) {
                    if (error.response?.status == 422) {
                        this.$notify.show({
                            type: 'error',
                            message: this.$t('global.errors.validationError'),
                        });
                    } else {
                        this.$notify.show({
                            type: 'error',
                            message: this.$t('global.errors.unknown'),
                        });

                        throw error;
                    }
                } finally {
                    this.saving = false;
                }
            },

            async onCreate() {
                this.user.accessible_accounts = [this.authUser.account.id];

                await this.createUser(this.user);

                this.$notify.show({
                    type: 'success',
                    message: this.$t('configs.rightPanel.users.userModal.successMessage'),
                });
            },

            async onUpdate() {
                await this.updateUser(this.user);

                this.$notify.show({
                    type: 'success',
                    message: this.$t('configs.rightPanel.users.userModal.updateMessage'),
                });
            },

            setPermissions(user) {
                this.user.permissions = user.permissions.map(permission => permission.name);
            },

            onOpen() {
                if (!this.isEdit) {
                    const permissions = [];

                    if (this.authUser.account.appointment_module) {
                        permissions.push('appointment module');
                    }

                    if (this.authUser.account.campaign_module) {
                        permissions.push('campaign module');
                    }

                    this.user.permissions = permissions;
                }

                this.user.organization_id = this.authUser.organization_id;
                this.user.timezone = this.authUser.account.timezone;
            },

            close() {
                this.$refs.modal.close();
            },

            onClosed() {
                merge(this.user, defaultUser);
                this.activeTab = 'profile';

                this.setEditingUser({});
            },
        },
    };
</script>
